/**
 * Shared formatting utilities for file views
 */

/**
 * Format a date string to localized date and time
 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

/**
 * Compact relative date: "now" / "5 minutes ago" / "3 hours ago" /
 * "2 days ago", falling back to a short localized date past a week.
 * Same shape as DriveView's list column.
 */
export function formatRelativeDate(isoString: string | null | undefined): string {
  if (!isoString) return '--'
  const date = new Date(isoString)
  const now = new Date()
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffSec < 60) return relativeFormatter.format(0, 'second')
  if (diffSec < 3600) return relativeFormatter.format(-Math.floor(diffSec / 60), 'minute')
  if (diffSec < 86400) return relativeFormatter.format(-Math.floor(diffSec / 3600), 'hour')
  if (diffSec < 604800) return relativeFormatter.format(-Math.floor(diffSec / 86400), 'day')

  return date.toLocaleDateString(undefined, {
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format file size in human-readable format
 */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
}
